import { useState, useEffect, useRef } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { formularyService } from '../services/formularyService'
import { medicationService } from '../services/medicationService'
import { MedicationReference } from '../types/medication'
import './SettingsFormulary.css'

export function SettingsFormulary() {
  const queryClient = useQueryClient()
  const [searchTerm, setSearchTerm] = useState('')
  const [searchResults, setSearchResults] = useState<MedicationReference[]>([])
  const [isSearching, setIsSearching] = useState(false)
  const [showAddForm, setShowAddForm] = useState(false)
  const searchTimeoutRef = useRef<number | undefined>()

  const { data: formularyMedications, isLoading, error, refetch: refetchFormulary } = useQuery({
    queryKey: ['formulary'],
    queryFn: () => formularyService.getFormularyMedications(),
  })

  // Fetch medication details for each formulary entry
  const { data: medicationDetails } = useQuery({
    queryKey: ['formulary-medication-details', formularyMedications?.map((f) => f.medicationId)],
    queryFn: async () => {
      if (!formularyMedications || formularyMedications.length === 0) return {}
      
      const details: Record<string, MedicationReference | null> = {}
      await Promise.all(
        formularyMedications.map(async (fm) => {
          try {
            // Try to get medication by drugIdPK (which is the medicationId in formulary)
            const searchResult = await medicationService.searchReference(fm.medicationId, {})
            if (searchResult.items.length > 0) {
              // Find exact match by drugIdPK
              const match = searchResult.items.find((m) => m.drugIdPK === fm.medicationId)
              details[fm.medicationId] = match || searchResult.items[0] || null
            } else {
              details[fm.medicationId] = null
            }
          } catch (err) {
            console.error(`Failed to fetch medication details for ${fm.medicationId}`, err)
            details[fm.medicationId] = null
          }
        }),
      )
      return details
    },
    enabled: !!formularyMedications && formularyMedications.length > 0,
  })

  const addMutation = useMutation({
    mutationFn: (medicationId: string) => formularyService.addToFormulary(medicationId),
    onSuccess: async (_data, medicationId) => {
      // Invalidate and refetch queries
      await queryClient.invalidateQueries({ queryKey: ['formulary'] })
      await queryClient.invalidateQueries({ queryKey: ['formulary-medication-details'] })
      // Refetch immediately to show the new medication
      await refetchFormulary()
      // Update search results to show "In Formulary" badge
      setSearchResults((prev) =>
        prev.map((med) =>
          med.drugIdPK === medicationId ? { ...med, _isInFormulary: true } : med
        )
      )
      // Don't close the form - let user see the medication was added
      // Clear search term so they can search for another
      setSearchTerm('')
    },
    onError: (error: any) => {
      alert(error?.message || 'Failed to add medication to formulary')
    },
  })

  const removeMutation = useMutation({
    mutationFn: (medicationId: string) => formularyService.removeFromFormulary(medicationId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['formulary'] })
      queryClient.invalidateQueries({ queryKey: ['formulary-medication-details'] })
    },
  })

  // Search for medications
  useEffect(() => {
    if (searchTimeoutRef.current !== undefined) {
      window.clearTimeout(searchTimeoutRef.current)
    }

    const term = searchTerm.trim()
    if (!term || term.length < 3) {
      setSearchResults([])
      setIsSearching(false)
      return
    }

    setIsSearching(true)
    const timeoutId = window.setTimeout(async () => {
      try {
        const result = await medicationService.searchReference(term)
        setSearchResults(result.items.slice(0, 20)) // Limit to 20 results
      } catch (err) {
        console.error('Failed to search medications', err)
        setSearchResults([])
      } finally {
        setIsSearching(false)
      }
    }, 300)

    searchTimeoutRef.current = timeoutId

    return () => {
      if (searchTimeoutRef.current !== undefined) {
        window.clearTimeout(searchTimeoutRef.current)
      }
    }
  }, [searchTerm])

  const handleAdd = async (medication: MedicationReference) => {
    if (window.confirm(`Add "${medication.productName}" to the practice formulary?`)) {
      try {
        await addMutation.mutateAsync(medication.drugIdPK)
      } catch (error: any) {
        // Error feedback is handled in onError callback
      }
    }
  }

  const handleRemove = (medicationId: string, medicationName?: string) => {
    const name = medicationName || medicationId
    if (window.confirm(`Remove "${name}" from the practice formulary?`)) {
      removeMutation.mutate(medicationId)
    }
  }

  if (isLoading) {
    return <div className="settings-formulary">Loading formulary medications...</div>
  }

  if (error) {
    return <div className="settings-formulary error">Error loading formulary: {String(error)}</div>
  }

  return (
    <div className="settings-formulary">
      <div className="settings-header">
        <h1>Practice Formulary</h1>
        <p>Manage your practice's preferred medications list. Medications in the formulary will show a checkmark when prescribing.</p>
      </div>

      <div className="formulary-actions">
        <button
          type="button"
          className="btn btn-primary"
          onClick={() => {
            setShowAddForm(!showAddForm)
            setSearchTerm('')
            setSearchResults([])
          }}
        >
          {showAddForm ? 'Cancel' : '+ Add Medication'}
        </button>
      </div>

      {showAddForm && (
        <div className="add-medication-form">
          <h3>Search and Add Medication</h3>
          <div className="search-box">
            <input
              type="text"
              className="form-input"
              placeholder="Search medications (minimum 3 characters)..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>

          {isSearching && <div className="search-status">Searching...</div>}

          {searchResults.length > 0 && (
            <div className="search-results">
              <h4>Search Results ({searchResults.length})</h4>
              <ul className="medication-list">
                {searchResults.map((med) => {
                  const isInFormulary =
                    (med as any)._isInFormulary ||
                    formularyMedications?.some((f) => f.medicationId === med.drugIdPK)
                  return (
                    <li key={med.drugIdPK} className="medication-item">
                      <div className="medication-info">
                        <div className="medication-name">{med.productName}</div>
                        <div className="medication-details">
                          {med.dosageForm} {med.strength && `- ${med.strength}`}
                          {med.gmsReimbursable && <span className="gms-badge">GMS</span>}
                        </div>
                        {med.atcCodes && med.atcCodes.length > 0 && (
                          <div className="medication-atc">ATC: {med.atcCodes.join(', ')}</div>
                        )}
                      </div>
                      <div className="medication-actions">
                        {isInFormulary ? (
                          <span className="in-formulary-badge">✓ In Formulary</span>
                        ) : (
                          <button
                            type="button"
                            className="btn btn-small btn-primary"
                            onClick={() => handleAdd(med)}
                            disabled={addMutation.isPending}
                          >
                            {addMutation.isPending ? 'Adding...' : 'Add to Formulary'}
                          </button>
                        )}
                      </div>
                    </li>
                  )
                })}
              </ul>
            </div>
          )}

          {searchTerm.length >= 3 && !isSearching && searchResults.length === 0 && (
            <div className="no-results">No medications found matching "{searchTerm}"</div>
          )}
        </div>
      )}

      <div className="formulary-list">
        <h3>Formulary Medications ({formularyMedications?.length || 0})</h3>
        {!formularyMedications || formularyMedications.length === 0 ? (
          <div className="empty-state">
            <p>No medications in the formulary yet.</p>
            <p>Click "Add Medication" above to start building your formulary.</p>
          </div>
        ) : (
          <ul className="medication-list">
            {formularyMedications.map((fm) => {
              const details = medicationDetails?.[fm.medicationId]
              return (
                <li key={fm.medicationId} className="medication-item">
                  <div className="medication-info">
                    {details ? (
                      <>
                        <div className="medication-name">{details.productName}</div>
                        <div className="medication-details">
                          {details.dosageForm} {details.strength && `- ${details.strength}`}
                          {details.gmsReimbursable && <span className="gms-badge">GMS</span>}
                        </div>
                        {details.atcCodes && details.atcCodes.length > 0 && (
                          <div className="medication-atc">ATC: {details.atcCodes.join(', ')}</div>
                        )}
                      </>
                    ) : (
                      <>
                        <div className="medication-name">{fm.medicationId}</div>
                        <div className="medication-details">Loading details...</div>
                      </>
                    )}
                    <div className="medication-meta">
                      Added: {new Date(fm.addedAt).toLocaleDateString()}
                    </div>
                  </div>
                  <div className="medication-actions">
                    <button
                      type="button"
                      className="btn btn-small btn-danger"
                      onClick={() => handleRemove(fm.medicationId, details?.productName)}
                      disabled={removeMutation.isPending}
                    >
                      Remove
                    </button>
                  </div>
                </li>
              )
            })}
          </ul>
        )}
      </div>
    </div>
  )
}
